import React from 'react';

const ShapeRenderer = ({ shape, pageWidth, pageHeight }) => {
  // Shape coordinates are in percentages (0-100) relative to the page
  // The container div already positions and sizes the shape correctly
  // So we render relative to the container (0,0 to 100%,100%)
  const x1 = shape.x1;
  const y1 = shape.y1;
  const x2 = shape.x2;
  const y2 = shape.y2;

  const width = Math.abs(x2 - x1);
  const height = Math.abs(y2 - y1);

  // Base style - fills the container which is already positioned correctly
  const baseStyle = {
    position: 'absolute',
    pointerEvents: 'none',
    zIndex: 5,
    left: '0%',
    top: '0%',
    width: '100%',
    height: '100%',
    boxSizing: 'border-box'
  };

  switch (shape.type) {
    case 'rectangle':
      // Rectangle fills the container
      return (
        <div
          style={{
            ...baseStyle,
            border: `2px solid ${shape.color}`,
            borderRadius: '2px'
          }}
        />
      );

    case 'circle':
      // Circle fits the container (use the larger dimension)
      const size = Math.max(width, height);
      const circleSize = (size / Math.max(width, height)) * 100;
      return (
        <div
          style={{
            ...baseStyle,
            left: '50%',
            top: '50%',
            width: `${circleSize}%`,
            height: `${circleSize}%`,
            transform: 'translate(-50%, -50%)',
            border: `2px solid ${shape.color}`,
            borderRadius: '50%'
          }}
        />
      );

    case 'line': {
      // Line should always start at (x1, y1) and extend towards (x2, y2)
      const dx = x2 - x1;
      const dy = y2 - y1;
      const lineAngle = Math.atan2(dy, dx) * (180 / Math.PI);
      const lineLength = Math.sqrt(dx * dx + dy * dy);
      const lineLengthPercent = lineLength;
      return (
        <div style={baseStyle}>
          <div
            style={{
              position: 'absolute',
              left: `${x1}%`,
              top: `${y1}%`,
              width: `${lineLengthPercent}%`,
              height: '2px',
              backgroundColor: shape.color,
              transformOrigin: '0 50%',
              transform: `translateY(-50%) rotate(${lineAngle}deg)`
            }}
          />
        </div>
      );
    }

    case 'arrow': {
      // Arrow should always start at (x1, y1) and point towards (x2, y2)
      const dx = x2 - x1;
      const dy = y2 - y1;
      const arrowAngle = Math.atan2(dy, dx) * (180 / Math.PI);
      const arrowLength = Math.sqrt(dx * dx + dy * dy);
      const arrowLengthPercent = arrowLength;
      return (
        <div style={baseStyle}>
          {/* Arrow shaft and head share the same rotated container */}
          <div
            style={{
              position: 'absolute',
              left: `${x1}%`,
              top: `${y1}%`,
              width: `${arrowLengthPercent}%`,
              height: '2px',
              backgroundColor: shape.color,
              transformOrigin: '0 50%',
              transform: `translateY(-50%) rotate(${arrowAngle}deg)`
            }}
          >
            {/* Arrowhead at the end of the line */}
            <div
              style={{
                position: 'absolute',
                right: 0,
                top: '50%',
                width: 0,
                height: 0,
                borderLeft: `8px solid ${shape.color}`,
                borderTop: '4px solid transparent',
                borderBottom: '4px solid transparent',
                transform: 'translateY(-50%)'
              }}
            />
          </div>
        </div>
      );
    }

    case 'highlight':
      // Highlight fills the container
      return (
        <div
          style={{
            ...baseStyle,
            backgroundColor: shape.color,
            opacity: 0.3
          }}
        />
      );

    default:
      return null;
  }
};

export default ShapeRenderer;
